import React, { useContext, useEffect, useMemo, useState } from 'react';
import loadable from '@loadable/component';
import { useLocation } from 'react-router-dom';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import styled, { keyframes, css } from 'styled-components';
import EmptyButton from 'ls-common-client/dist/components/EmptyButton';
import UserMenu from 'ls-common-client/dist/components/UserMenu';
import Icon from 'ls-common-client/dist/components/Icon';
import Container from 'ls-common-client/dist/components/Container';
import { Context } from '../../../context/AppContext';
import { MODALS, ENABLE_USER_MANAGEMENT } from '../../../constants';
import imageResizer from '../../../services/imageResizer';
import LogoutModal from './LogoutModal';
import useFavourites from '../../../hooks/useFavourites';
import useDataLayerPush from '../../../hooks/useDataLayerPush';
import { DATA_PUSH } from '../../Search/constants';
import appendUTMParams from '../../../utils/utmParams';
import useAppMenu from '../../../hooks/useAppMenu';

const SaveButton = loadable(() => import('../../SaveButton'));

const StyledSearchIcon = styled(EmptyButton)`
  background: #ffffff;
  border-radius: 100%;
  width: 34px;
  height: 34px;
  margin-left: 5px;
  margin-right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  i {
    color: ${props => props.theme.primary.normal};
    height: 30px;
  }
`;

const growAnimation = keyframes`
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(0.9);
  }
  80% {
    transform: scale(0.6);
  }
  100% {
    transform: scale(1);
  }
`;

const shrinkAnimation = keyframes`
  0% {
    transform: scale(1);
  }
  70% {
    transform: scale(0.1);
  }
  90% {
    transform: scale(0.3);
  }
  100% {
    transform: scale(0);
  }
`;

const StyledHeartIcon = styled(Container)`
  ${props =>
    props.grow
      ? css`
          animation-name: ${growAnimation};
        `
      : ''}
  ${props =>
    props.shrink
      ? css`
          animation-name: ${shrinkAnimation};
        `
      : ''}
  animation-duration: 0.5s;
  animation-timing-function: linear;
  height: 21px;
  transform: ${props => (props.isFavourite ? 'scale(1)' : 'scale(0)')};
`;

const LoginMenu = ({ profile, searchIcon, is404 = false }) => {
  const {
    user: { user, isStaff, isPersoniv, loading },
    modal: { showModal },
    media: { desktop, mobile },
    auth,
    isServer,
  } = useContext(Context);

  const { appMenuHtml } = useAppMenu({
    isStaff,
    isPersoniv,
    loading,
  });

  const favourites = useFavourites();
  const { id: userId } = user || {};

  const { pathname } = useLocation();
  const pageType = pathname === '/' ? 'Home Page' : pathname;
  const isProfile = useMemo(() => pathname.includes('/profile'), [pathname]);

  const { dataLayerPush } = useDataLayerPush();

  const modalName = MODALS.USER_LOGOUT;

  const [dataLayer, setDataLayer] = useState([]);

  const onDataLayerPush = menuName => {
    if (user) {
      dataLayerPush({
        event: 'User Menu Selection',
        menu_name: menuName,
        submenu_name: '',
        cta_position_on_page: 'Header Slide Menu',
      });
    } else {
      setDataLayer(prev => [...prev, { menuName }]);
    }
  };
  const handleDataLayerPush = eventName => {
    dataLayerPush({
      event: eventName,
      cta_position_on_page: DATA_PUSH.CTA.PAGE_HEADER,
      page_type: pageType,
    });
  };

  useEffect(() => {
    if (user && dataLayer.length > 0) {
      // Push pending menu selections to the data layer
      dataLayer.forEach(({ menuName }) => {
        onDataLayerPush(menuName);
      });
      setDataLayer([]);
    }
  }, [user, dataLayer]);

  const menuItems = useMemo(() => {
    if (ENABLE_USER_MANAGEMENT && auth.token()) {
      return [
        {
          text: 'Saved',
          icon: 'ls-icon icon-heartoutline Header__UserMenu--Icons',
          onClick: () => {
            onDataLayerPush('Saved');
            window.open(appendUTMParams('/saved'), '_blank');
          },
        },
        {
          splitter: true,
        },
        {
          text: 'Reviews',
          icon: 'ls-icon icon-generalstarempty Header__UserMenu--Icons',
          onClick: () => {
            onDataLayerPush('Reviews');
            window.open(appendUTMParams('/reviews'), '_blank');
          },
        },
        {
          splitter: true,
        },
        {
          text: 'Enquiries',
          icon: 'ls-icon icon-generaledit Header__UserMenu--Icons',
          onClick: () => {
            onDataLayerPush('Enquiries');
            window.open(appendUTMParams('/enquiries'), '_blank');
          },
        },
        {
          splitter: true,
        },
        {
          text: 'Quotes',
          icon:
            'ls-icon icon-categorycarparkpayanddisplay Header__UserMenu--Icons',
          onClick: () => {
            onDataLayerPush('Quotes');
            window.open(appendUTMParams('/quotes'), '_blank');
          },
        },
      ];
    }
    return [];
  }, [auth]);

  return (
    <>
      {!!searchIcon && (
        <StyledSearchIcon
          onClick={() =>
            showModal(MODALS.SEARCH_AUTOSUGGEST, {
              inputFocus: 'keyword',
            })
          }
        >
          <Icon className="ls-icon icon-generalsearchlarge" />
        </StyledSearchIcon>
      )}
      {isProfile && !desktop && !is404 && (
        <SaveButton favourites={favourites} profile={profile} userId={userId}>
          {({ onClick, isFavourite, grow, shrink }) => {
            return (
              <EmptyButton
                onClick={onClick}
                marginRight="10px"
                height="34px"
                position="relative"
                display="flex"
                alignItems="flex-end"
              >
                <Icon
                  className="ls-icon icon-heartcutout"
                  color="white"
                  fontSize="34px"
                />
                <Container
                  position="absolute"
                  left="50%"
                  top="50%"
                  transform="translate(-50%,-50%)"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <StyledHeartIcon
                    isFavourite={isFavourite}
                    grow={grow}
                    shrink={shrink}
                  >
                    <Icon
                      fontSize="18px"
                      iconColor="#f16159"
                      className="ls-icon icon-heartsolid"
                      marginTop="2px"
                    />
                  </StyledHeartIcon>
                </Container>
              </EmptyButton>
            );
          }}
        </SaveButton>
      )}
      {auth.token() && (
        <Container
          dangerouslySetInnerHTML={{ __html: appMenuHtml }}
          width="34px"
          height="34px"
          transition="none"
        />
      )}
      <UserMenu
        marginLeft="15px"
        auth={{ ...auth, logout: () => showModal(modalName) }}
        imageResizer={imageResizer}
        user={user}
        onMenuItemClick={eventName => {
          handleDataLayerPush(eventName);
        }}
        menuItems={menuItems}
        onOpen={mobile ? disableBodyScroll : undefined}
        onClose={mobile ? enableBodyScroll : undefined}
      />
      <LogoutModal />
    </>
  );
};

export default LoginMenu;
